<template>
  <div>
    <NavbarTwo />

    <div v-if="loading" class="blog-post-area ptb-100 text-center text-silver">
      Loading...
    </div>

    <div v-else-if="!post" class="blog-post-area ptb-100 text-center text-silver">
      Post not found.
    </div>

    <template v-else>
      <div
        class="blog-post-hero"
        :style="post.cover_image ? { backgroundImage: `url(${post.cover_image})` } : {}"
      >
        <div class="blog-post-hero-overlay">
          <div class="container mw-1356">
            <div class="blog-post-hero-inner">
              <p class="blog-post-date">{{ formatDate(post.published_at) }}</p>
              <h1 class="blog-post-title">{{ post.title }}</h1>
              <p v-if="post.excerpt" class="blog-post-excerpt">{{ post.excerpt }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="blog-post-area">
        <div class="container mw-900">
          <div class="blog-content" v-html="renderedContent"></div>

          <div class="blog-post-footer">
            <RouterLink to="/blog" class="btn">
              <span class="border-style"><i class="ti ti-arrow-left"></i> Back to Blog</span>
            </RouterLink>
            <RouterLink to="/pricing" class="btn ms-2">
              <span class="border-style">Get IPTV Access</span>
            </RouterLink>
          </div>
        </div>
      </div>
    </template>

    <MainFooter />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import api from "@/services/api";
import { useSeo } from "@/composables/useSeo";
import { renderPostContent } from "@/utils/blogContent";

export default defineComponent({
  name: "BlogPostPage",
  components: { NavbarTwo, MainFooter },
  setup() {
    const route = useRoute();
    const post = ref<any>(null);
    const loading = ref(true);

    async function loadPost(slug: string) {
      loading.value = true;
      post.value = null;
      try {
        const { data } = await api.get(`/api/v1/blog/${slug}`);
        post.value = data.data;
      } catch {
        post.value = null;
      } finally {
        loading.value = false;
      }
    }

    useSeo({
      title: post.value?.meta_title || post.value?.title || "Blog",
      description: post.value?.meta_description || post.value?.excerpt || "",
      keywords: post.value?.meta_keywords || "",
      url: `/blog/${route.params.slug}`,
    });

    const renderedContent = computed(() => renderPostContent(post.value?.content));

    const formatDate = (iso: string) =>
      iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

    onMounted(() => loadPost(route.params.slug as string));
    watch(() => route.params.slug, (slug) => slug && loadPost(slug as string));

    return { post, loading, renderedContent, formatDate };
  },
});
</script>

<style scoped>
.blog-post-hero {
  width: 100%;
  min-height: 380px;
  background: #1e1112 center/cover no-repeat;
  position: relative;
}
.blog-post-hero-overlay {
  min-height: 380px;
  background: linear-gradient(to bottom, rgba(10,1,2,0.55) 0%, rgba(10,1,2,0.92) 100%);
  display: flex; align-items: flex-end; padding-bottom: 3rem;
}
.blog-post-hero-inner { max-width: 800px; }
.blog-post-date { color: #c90e0e; font-size: 0.82rem; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 0.5rem; }
.blog-post-title { color: #fff; font-size: clamp(1.6rem, 4vw, 2.6rem); font-weight: 900; line-height: 1.2; margin-bottom: 0.75rem; }
.blog-post-excerpt { color: #c0c0c0; font-size: 1.05rem; line-height: 1.7; }

.blog-post-area { background: #0a0102; padding: 3.5rem 0 5rem; }
.blog-post-footer { margin-top: 3rem; padding-top: 2rem; border-top: 1px solid #2a1a1b; }
</style>

<style>
/* Global — styles rendered v-html content */
.blog-content {
  color: #d0d0d0;
  font-size: 1.05rem;
  line-height: 1.85;
}
.blog-content h1,
.blog-content h2,
.blog-content h3,
.blog-content h4 {
  color: #ffffff;
  font-weight: 800;
  margin: 2rem 0 1rem;
  line-height: 1.3;
}
.blog-content h1 { font-size: 2rem; }
.blog-content h2 { font-size: 1.6rem; border-bottom: 2px solid #c90e0e; padding-bottom: 0.4rem; }
.blog-content h3 { font-size: 1.25rem; color: #ff6b6b; }
.blog-content h4 { font-size: 1.05rem; }
.blog-content p { margin-bottom: 1.25rem; }
.blog-content a { color: #c90e0e; text-decoration: underline; }
.blog-content a:hover { color: #ff6b6b; }
.blog-content strong, .blog-content b { color: #fff; font-weight: 700; }
.blog-content em, .blog-content i { font-style: italic; color: #e0d0d0; }
.blog-content ul, .blog-content ol { padding-left: 1.5rem; margin-bottom: 1.25rem; }
.blog-content li { margin-bottom: 0.4rem; }
.blog-content blockquote {
  border-left: 4px solid #c90e0e;
  margin: 1.5rem 0;
  padding: 0.75rem 1.25rem;
  background: #160d0e;
  border-radius: 0 8px 8px 0;
  color: #c0c0c0;
  font-style: italic;
}
.blog-content img {
  max-width: 100%;
  height: auto;
  border-radius: 10px;
  margin: 1.5rem 0;
  display: block;
}
.blog-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.95rem;
}
.blog-content th {
  background: #c90e0e;
  color: #fff;
  padding: 0.6rem 0.9rem;
  text-align: left;
}
.blog-content td {
  padding: 0.6rem 0.9rem;
  border-bottom: 1px solid #2a1a1b;
  color: #d0d0d0;
}
.blog-content tr:nth-child(even) td { background: #140d0e; }
.blog-content code {
  background: #1e1112;
  color: #ff9999;
  padding: 0.15em 0.4em;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em;
}
.blog-content pre {
  background: #1e1112;
  border: 1px solid #2a1a1b;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}
.blog-content pre code { background: none; padding: 0; color: #d0d0d0; }
.blog-video-wrap {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 12px;
  margin: 1.5rem 0;
}
.blog-video-wrap iframe {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  border: none;
}
</style>
